import { useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Community } from "@graphql/api";

import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";

import { Button, LoadingOrValue, Modal } from "@covertree/ui";
import { TableView } from "@covertree/ui/table/TableView";
import { Toast } from "@covertree/utils";

import { useToggleCommunityActiveStatusMutation } from "@redux/portfolios/portfoliosApi";

interface Props {
  communities: Community[];
  isError: boolean;
  isLoading: boolean;
}

export function PortfolioDetailsContent({
  communities,
  isError,
  isLoading,
}: Props) {
  const [selectedCommunity, setSelectedCommunity] = useState<Community>();
  const [toggleCommunityActiveStatus, { isLoading: isStatusChangeLoading }] =
    useToggleCommunityActiveStatusMutation();

  const handleStatusChange = async () => {
    if (!selectedCommunity) return;

    const action = selectedCommunity.isActive ? "deactivated" : "activated";
    try {
      await toggleCommunityActiveStatus({
        portfolioId: selectedCommunity.portfolio.id,
        propertyId: selectedCommunity.propertyId,
      }).unwrap();
      setSelectedCommunity(undefined);
      Toast.showSuccess(`Community ${action}`);
    } catch {
      setSelectedCommunity(undefined);
      Toast.showError(`Unable to ${action.slice(0, -1)} community`);
    }
  };

  const firstPortfolio = communities?.[0]?.portfolio;

  if (isError) {
    return <Typography pt={2}>Error loading portfolio details.</Typography>;
  }

  if (!isLoading && (!communities || communities.length === 0)) {
    return (
      <Typography pt={2}>
        No communities found. If the portfolio was just added, it means the sync
        is still in progress. Please wait for the Slack notification to resume
        Portfolio onboarding process.
      </Typography>
    );
  }

  const portfolio = firstPortfolio ?? {
    id: "",
    name: "",
    isActive: false,
    integrationType: undefined,
  };

  return (
    <>
      {selectedCommunity && (
        <Modal
          open
          disableClose={isStatusChangeLoading}
          title={`${selectedCommunity.isActive ? "Deactivate" : "Activate"} Community`}
          onClose={() => setSelectedCommunity(undefined)}
          maxWidth="sm">
          <Stack>
            <Typography>
              Are you sure you want to{" "}
              {selectedCommunity.isActive ? "deactivate" : "activate"}{" "}
              <strong>{selectedCommunity.name}</strong>?
            </Typography>
            {selectedCommunity.isActive && (
              <Typography mt={2}>
                All current residents in this community will be moved to former
                resident status. This does not permanently exclude them.
              </Typography>
            )}
            <Box p={4} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleStatusChange}
                disabled={isStatusChangeLoading}
                loading={isStatusChangeLoading}>
                Confirm
              </Button>
              <Button
                variant="outlined"
                disabled={isStatusChangeLoading}
                onClick={() => setSelectedCommunity(undefined)}
                sx={{ ml: 2 }}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </Modal>
      )}
      <Box pt={2}>
        <DisplayPropertiesCard
          properties={[
            {
              label: "Portfolio ID",
              value: (
                <LoadingOrValue
                  loading={isLoading}
                  value={portfolio.id}
                  height={28}
                  width={150}
                />
              ),
              gridSize: 6,
            },
            {
              label: "Portfolio Name",
              value: (
                <LoadingOrValue
                  loading={isLoading}
                  value={portfolio.name}
                  height={28}
                  width={150}
                />
              ),
              gridSize: 6,
            },
            {
              label: "Status",
              value: (
                <LoadingOrValue
                  loading={isLoading}
                  value={portfolio.isActive ? "Active" : "Inactive"}
                  height={28}
                  width={150}
                />
              ),
              gridSize: 6,
            },
            {
              label: "Integration Type",
              value: (
                <LoadingOrValue
                  loading={isLoading}
                  value={portfolio.integrationType ?? "None"}
                  height={28}
                  width={150}
                />
              ),
              gridSize: 6,
            },
          ]}
        />
      </Box>
      <Box my={2}>
        <TableView
          header="Communities"
          page={1}
          showPagination={false}
          rowsPerPage={communities?.length ?? 0}
          totalCount={(communities ?? []).length}
          rows={(communities ?? []).map((item) => ({
            id: item.ctmhcId,
            actionMenuItems: [
              {
                label: item.isActive
                  ? "Deactivate community"
                  : "Activate community",
                onClick: () => setSelectedCommunity(item),
              },
            ],
            data: [item.propertyId, item.name, item.isActive ? "Yes" : "No"],
          }))}
          columns={[
            { headerName: "ID" },
            { headerName: "Name" },
            { headerName: "Active?" },
          ]}
          onPageChange={() => {}}
          error={isError}
          loading={isLoading}
          maxTableHeight="60vh"
        />
      </Box>
    </>
  );
}
